import { useState } from 'react';
import { useAppDispatch } from 'hooks';
import { showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { JobStatus, PushScheduleBy } from '../constant';
import { BulkActionType } from '../constant';
import {
  useUpdateJobAssigneesBulkMutation,
  useUpdateJobScheduleBulkMutation,
  useUpdateJobStatusBulkMutation,
  useUpdateJobTagsBulkMutation
} from '../api';
import {
  BulkActionResponse,
  BulkActionState,
  BulkReassignParams,
  BulkRescheduleParams,
  BulkStatusUpdateParams,
  BulkTagsUpdateParams
} from '../types/bulkActions';
import { Tag } from 'containers/tags/tags.model';
import { JobQueryParams } from './usePaginatedGetJobsQuery';

interface UseBulkActionsProps {
  teamId?: number;
  selectedJobIds: string[];
  isSelectAllAcrossPagination: boolean;
  deselectedJobIds: string[];
  onActionComplete: () => void;
  queryParams: JobQueryParams;
  searchType: any;
}

export const useBulkActions = ({
  teamId,
  selectedJobIds,
  isSelectAllAcrossPagination,
  deselectedJobIds,
  onActionComplete,
  queryParams,
  searchType
}: UseBulkActionsProps) => {
  const dispatch = useAppDispatch();
  const [bulkActionState, setBulkActionState] = useState<BulkActionState | null>(null);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);

  // Create filter object once and reuse across all bulk actions
  const {
    isUnscheduled,
    viewId: _viewId,
    limit: _limit,
    groupByStatus: _groupByStatus,
    sortBy: _sortBy,
    sortOrder: _sortOrder,
    search,
    ...restQueryParams
  } = queryParams;
  const isSearchPresent = search !== null && search !== '';

  const filterParams: any = {};

  // Only include parameters if they are present, similar to api.ts pattern
  Object.keys(restQueryParams).forEach(key => {
    if (Array.isArray(restQueryParams[key])) {
      if (restQueryParams[key].length > 0) {
        filterParams[key] = restQueryParams[key]?.join(',');
      }
    } else {
      if (restQueryParams[key] !== null) {
        filterParams[key] = restQueryParams[key];
      }
    }
  });

  // Add isUnscheduled if present
  if (isUnscheduled) {
    filterParams.isUnscheduled = true;
  }

  if (isSearchPresent) {
    filterParams[searchType.type] = search.trim();
  }

  const [updateJobScheduleBulk, { isLoading: isUpdatingJobScheduleBulk }] = useUpdateJobScheduleBulkMutation();
  const [updateJobStatusBulk, { isLoading: isUpdatingJobStatusBulk }] = useUpdateJobStatusBulkMutation();
  const [updateJobAssigneesBulk, { isLoading: isUpdatingJobAssigneesBulk }] = useUpdateJobAssigneesBulkMutation();
  const [updateJobTagsBulk, { isLoading: isUpdatingJobTagsBulk }] = useUpdateJobTagsBulkMutation();

  const handleBulkActionResponse = (
    response: BulkActionResponse,
    isConfirmed: boolean,
    actionType: BulkActionType,
    actionParams: any,
    successMessage: string
  ) => {
    if (response.data.showPopup && !isConfirmed && !response.data.updateCompleted) {
      setBulkActionState({
        actionType,
        totalJobs: selectedJobIds.length,
        actionParams,
        possibleJobCount: response.data.updatePossibleJobCount,
        notEditableCounts: {
          noPermissionToEditJobCount: response.data.noPermissionToEditJobCount,
          notEditableWithRespectToPayrunJobCount: response.data.notEditableWithRespectToPayrunJobCount,
          noPermissionToModifyDoneJobCount: response.data.noPermissionToModifyDoneJobCount,
          noPermissionToMarkJobAsDoneCount: response.data.noPermissionToMarkJobAsDoneCount,
          noScheduleJobCount: response.data.noScheduleJobCount
        }
      });
      setShowBulkActionModal(true);
      return false;
    }

    if (response.data.updateCompleted) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: successMessage
          },
          showClose: true,
          type: NotifierTypes.SUCCESS
        })
      );
      onActionComplete();
      return true;
    }

    return false;
  };

  const handleRescheduleJobs = async (
    rescheduleParams: {
      scheduledFrom?: string;
      scheduledTo?: string;
      pushScheduleBy?: PushScheduleBy;
      pushScheduleByValue?: number;
    },
    isConfirmed = false
  ) => {
    try {
      const params: BulkRescheduleParams = {
        jobIds: isSelectAllAcrossPagination ? [] : selectedJobIds,
        ...(rescheduleParams?.pushScheduleBy &&
          rescheduleParams?.pushScheduleByValue && {
            pushScheduleBy: rescheduleParams.pushScheduleBy,
            pushScheduleByValue: rescheduleParams.pushScheduleByValue
          }),
        ...(rescheduleParams?.scheduledFrom &&
          rescheduleParams?.scheduledTo && {
            scheduledFrom: rescheduleParams.scheduledFrom,
            scheduledTo: rescheduleParams.scheduledTo
          }),
        ...(isSelectAllAcrossPagination && {
          isSelectedAll: true,
          excludedJobIds: deselectedJobIds
        }),
        ...(isConfirmed && { isConfirmed: true }),
        filter: filterParams
      };

      const response = await updateJobScheduleBulk({
        teamId,
        body: params
      }).unwrap();

      return handleBulkActionResponse(
        response,
        isConfirmed,
        BulkActionType.RESCHEDULE,
        rescheduleParams,
        'Jobs rescheduled successfully'
      );
    } catch (error) {
      return false;
    }
  };

  const handleChangeJobStatus = async (status: JobStatus, isConfirmed = false) => {
    try {
      const params: BulkStatusUpdateParams = {
        jobIds: isSelectAllAcrossPagination ? [] : selectedJobIds,
        status,
        ...(isSelectAllAcrossPagination && {
          isSelectedAll: true,
          excludedJobIds: deselectedJobIds
        }),
        ...(isConfirmed && { isConfirmed: true }),
        filter: filterParams
      };

      const response = await updateJobStatusBulk({
        teamId,
        body: params
      }).unwrap();

      return handleBulkActionResponse(
        response,
        isConfirmed,
        BulkActionType.STATUS,
        { status },
        'Jobs status updated successfully'
      );
    } catch (error) {
      return false;
    }
  };

  const handleReassignJobs = async (assignees: string[], serviceAreaId?: string, isConfirmed = false) => {
    try {
      const params: BulkReassignParams = {
        jobIds: isSelectAllAcrossPagination ? [] : selectedJobIds,
        assignees,
        ...(serviceAreaId && { serviceAreaId }),
        ...(isSelectAllAcrossPagination && {
          isSelectedAll: true,
          excludedJobIds: deselectedJobIds
        }),
        ...(isConfirmed && { isConfirmed: true }),
        filter: filterParams
      };

      const response = await updateJobAssigneesBulk({
        teamId,
        body: params
      }).unwrap();

      return handleBulkActionResponse(
        response,
        isConfirmed,
        BulkActionType.REASSIGN,
        { assignees, serviceAreaId },
        'Jobs reassigned successfully'
      );
    } catch (error) {
      return false;
    }
  };

  const handleUpdateTags = async (tagsToAdd: Tag[], removeCurrentTags: boolean) => {
    try {
      const params: BulkTagsUpdateParams = {
        jobIds: isSelectAllAcrossPagination ? [] : selectedJobIds,
        tagIds: tagsToAdd.map(tag => tag.id),
        removeExistingTags: removeCurrentTags,
        ...(isSelectAllAcrossPagination && {
          isSelectedAll: true,
          excludedJobIds: deselectedJobIds
        }),
        filter: filterParams
      };

      await updateJobTagsBulk({
        teamId,
        body: params
      }).unwrap();

      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Tags updated successfully',
            isMessageHtml: false
          },
          showClose: true,
          type: NotifierTypes.SUCCESS
        })
      );
      onActionComplete();
      setShowBulkActionModal(false);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleBulkActionModalClose = () => {
    setShowBulkActionModal(false);
    setBulkActionState(null);
  };

  const handleBulkActionConfirm = async () => {
    if (!bulkActionState) return;

    let success = false;
    switch (bulkActionState.actionType) {
      case BulkActionType.RESCHEDULE:
        if (bulkActionState.actionParams) {
          success = await handleRescheduleJobs(
            {
              scheduledFrom: bulkActionState.actionParams?.scheduledFrom,
              scheduledTo: bulkActionState.actionParams?.scheduledTo,
              pushScheduleBy: bulkActionState.actionParams?.pushScheduleBy,
              pushScheduleByValue: bulkActionState.actionParams?.pushScheduleByValue
            },
            true
          );
        }
        break;
      case BulkActionType.STATUS:
        if (bulkActionState.actionParams?.status) {
          success = await handleChangeJobStatus(bulkActionState.actionParams.status, true);
        }
        break;
      case BulkActionType.REASSIGN:
        if (bulkActionState.actionParams?.assignees) {
          success = await handleReassignJobs(
            bulkActionState.actionParams.assignees,
            bulkActionState.actionParams.serviceAreaId,
            true
          );
        }
        break;
    }

    if (success) {
      handleBulkActionModalClose();
    }
  };

  const isActionUpdating =
    isUpdatingJobScheduleBulk || isUpdatingJobStatusBulk || isUpdatingJobAssigneesBulk || isUpdatingJobTagsBulk;

  return {
    bulkActionState,
    showBulkActionModal,
    isActionUpdating,
    handleRescheduleJobs,
    handleChangeJobStatus,
    handleReassignJobs,
    handleUpdateTags,
    handleBulkActionModalClose,
    handleBulkActionConfirm
  };
};
